/**
 * Morgenerinnerung — Karte im Profil.
 *
 * Eingeschaltet wird je Gerät: die Erinnerung soll dort ankommen, wo man
 * morgens stempelt, und das weiss nur die Person selbst. Uhrzeit und Pause
 * gelten dagegen für alle ihre Geräte.
 */

import { useCallback, useEffect, useState } from 'react';
import { addDays, dateLabel, dayKey, de, dowLabel, fullDateLabel, type DayKey } from '@zeit/domain';
import { ApiError, api, type ReminderStatus } from '../lib/appwrite.ts';
import { serverNow } from '../lib/clock.ts';
import {
  currentSubscription, deviceLabel, permission, pushSupport, subscribe, unsubscribe,
} from '../lib/push.ts';
import { Card, ErrorText, Field } from '../ui/index.tsx';

const r = de.reminder;

function dayName(day: DayKey, today: DayKey): string {
  if (day === today) return r.today;
  if (day === addDays(today, 1)) return r.tomorrow;
  return `${dowLabel(day)}, ${dateLabel(day)}`;
}

export function ReminderCard({ onToast }: { onToast: (text: string, tone?: 'error') => void }) {
  const support = pushSupport();
  const today = dayKey(serverNow());

  const [status, setStatus] = useState<ReminderStatus | null>(null);
  const [endpoint, setEndpoint] = useState('');
  const [time, setTime] = useState('');
  const [pauseUntil, setPauseUntil] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const adopt = useCallback((next: ReminderStatus) => {
    setStatus(next);
    setTime(next.time);
    setPauseUntil(next.pausedUntil);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const sub = await currentSubscription();
        const res = await api.reminders.status(sub?.endpoint);
        if (cancelled) return;
        setEndpoint(sub?.endpoint ?? '');
        adopt(res);
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : de.error.generic);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adopt]);

  /** Gemeinsamer Rahmen: sperren, Fehler zeigen, Erfolg melden. */
  const run = async (fn: () => Promise<string | null>) => {
    setBusy(true);
    setError('');
    try {
      const done = await fn();
      if (done) onToast(done);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : de.error.generic);
    } finally {
      setBusy(false);
    }
  };

  const enable = () =>
    run(async () => {
      const sub = await subscribe(status!.publicKey);
      if (!sub) {
        setError(r.denied);
        return null;
      }
      adopt(await api.reminders.subscribe(sub.toJSON(), deviceLabel()));
      setEndpoint(sub.endpoint);
      return r.enabled;
    });

  const disable = () =>
    run(async () => {
      const gone = (await unsubscribe()) ?? endpoint;
      if (gone) adopt(await api.reminders.unsubscribe(gone));
      setEndpoint('');
      return r.disabled;
    });

  const sendTest = () =>
    run(async () => {
      await api.reminders.test(endpoint);
      return r.testSent;
    });

  const save = (input: { time?: string; pausedUntil?: string }) =>
    run(async () => {
      adopt(await api.reminders.settings({ ...input, endpoint }));
      return de.toast.saved;
    });

  const active = Boolean(status?.thisDevice && endpoint);
  const paused = status?.pausedUntil && status.pausedUntil >= today ? status.pausedUntil : '';

  return (
    <Card
      title={r.title}
      aside={
        status && (
          <span className={`tag ${active ? 'tag-accent' : 'tag-neutral'}`}>{active ? r.on : r.off}</span>
        )
      }
    >
      <p className="text-muted" style={{ fontSize: 13, maxWidth: '60ch', margin: 0 }}>
        {r.intro}
      </p>

      {support === 'ios-install' && <p style={{ fontSize: 13, maxWidth: '60ch', margin: 0 }}>{r.iosInstall}</p>}
      {support === 'unsupported' && <p style={{ fontSize: 13, margin: 0 }}>{r.unsupported}</p>}
      {support === 'ok' && permission() === 'denied' && !active && (
        <p style={{ fontSize: 13, maxWidth: '60ch', margin: 0 }}>{r.denied}</p>
      )}

      <ErrorText>{error}</ErrorText>

      {status && (
        <>
          {support === 'ok' && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {active ? (
                <>
                  <button type="button" className="btn btn-secondary" disabled={busy} onClick={disable}>
                    {r.disable}
                  </button>
                  <button type="button" className="btn btn-ghost" disabled={busy} onClick={sendTest}>
                    {r.test}
                  </button>
                </>
              ) : (
                <button type="button" className="btn btn-primary" disabled={busy} onClick={enable}>
                  {r.enable}
                </button>
              )}
            </div>
          )}

          {status.otherDevices > 0 && (
            <div className="text-muted" style={{ fontSize: 12 }}>{r.otherDevices(status.otherDevices)}</div>
          )}

          {(active || status.otherDevices > 0) && (
            <>
              <div style={{ fontSize: 13 }}>
                {paused
                  ? r.paused(fullDateLabel(paused))
                  : status.next
                    ? r.next(dayName(status.next.day, today), status.next.time)
                    : r.noNext}
              </div>

              <div style={{ display: 'grid', gap: 14, maxWidth: 360 }}>
                <Field label={r.time} htmlFor="reminder-time" hint={r.timeHint(status.detected)}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      id="reminder-time"
                      className="input"
                      type="time"
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      style={{ minHeight: 44, flex: 1 }}
                    />
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={busy || time === status.time}
                      onClick={() => save({ time })}
                    >
                      {r.save}
                    </button>
                  </div>
                </Field>

                <Field label={r.pause} htmlFor="reminder-pause">
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      id="reminder-pause"
                      className="input"
                      type="date"
                      min={today}
                      value={pauseUntil}
                      onChange={(e) => setPauseUntil(e.target.value)}
                      style={{ minHeight: 44, flex: 1 }}
                    />
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={busy || pauseUntil === status.pausedUntil}
                      onClick={() => save({ pausedUntil: pauseUntil })}
                    >
                      {r.save}
                    </button>
                  </div>
                </Field>

                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {paused ? (
                    <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => save({ pausedUntil: '' })}>
                      {r.pauseClear}
                    </button>
                  ) : (
                    <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => save({ pausedUntil: today })}>
                      {r.pauseToday}
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </Card>
  );
}
